import { z } from 'zod';
import { idSchema, isoDateTimeSchema, moneySchema, positiveMoneySchema } from './common';
import { fxSnapshotSchema, manualFxSchema } from './expenses';

export const settlementMethodSchema = z.enum(['paypal', 'revolut', 'bizum', 'cash', 'other']);
export type SettlementMethod = z.infer<typeof settlementMethodSchema>;

/**
 * - `pending`: recorded by one side, waiting for the other side; counts in balances.
 * - `confirmed`: the other side confirmed (or the other side is a placeholder); counts.
 * - `disputed`: "Not received"; does NOT count (balance restored).
 * - `cancelled`: withdrawn by its creator while pending; does NOT count.
 */
export const settlementStatusSchema = z.enum(['pending', 'confirmed', 'disputed', 'cancelled']);
export type SettlementStatus = z.infer<typeof settlementStatusSchema>;

/**
 * `POST /api/groups/:id/settlements`. The caller must be `fromMemberId` ("Mark as paid") or
 * `toMemberId` ("Record a payment you received"); the other side is asked to confirm.
 */
export const createSettlementRequestSchema = z.object({
  /** Optional client-generated id (offline-first); retries with the same id are idempotent. */
  id: idSchema.optional(),
  fromMemberId: idSchema,
  toMemberId: idSchema,
  amount: positiveMoneySchema,
  method: settlementMethodSchema,
  note: z.string().trim().max(200).nullable().optional(),
  /** Manual rate into the group currency; ECB is used when omitted. */
  fx: manualFxSchema.nullable().optional(),
});
export type CreateSettlementRequest = z.input<typeof createSettlementRequestSchema>;

export const settlementSchema = z.object({
  id: idSchema,
  groupId: idSchema,
  fromMemberId: idSchema,
  toMemberId: idSchema,
  amount: moneySchema,
  fx: fxSnapshotSchema.nullable(),
  /** `amount` in the group currency when it differs. */
  convertedAmount: moneySchema.nullable(),
  method: settlementMethodSchema,
  note: z.string().nullable(),
  status: settlementStatusSchema,
  createdBy: idSchema,
  createdAt: isoDateTimeSchema,
  respondedBy: idSchema.nullable(),
  respondedAt: isoDateTimeSchema.nullable(),
  /** User who must confirm / reject it while pending (`null` otherwise). */
  awaitingUserId: idSchema.nullable(),
});
export type Settlement = z.infer<typeof settlementSchema>;

export const settlementResponseSchema = z.object({ settlement: settlementSchema });
export type SettlementResponse = z.infer<typeof settlementResponseSchema>;

/** Seconds after "Not received" during which `undo-reject` is accepted (Undo toast is 6 s). */
export const REJECT_UNDO_WINDOW_SECONDS = 30;
